import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {MatCardModule} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {
  UpdateBasicPlayerInfoComponent
} from "../../dialogs/update-basic-player-info/update-basic-player-info.component";
import {IDialogGenericData} from "../../../interfaces/IDialogGenericData";
import {
  ConfirmationActionDialogComponent
} from "../../dialogs/confirmation-action-dialog/confirmation-action-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {DialogFunctionService} from "../../../services/dialog-function.service";
import {Router} from "@angular/router";
import {IGamePlayerInputData} from "../../../interfaces/player/IGamePlayerInputData";
import {IGeneralPlayerInfo} from "../../../interfaces/player/IGeneralPlayerInfo";
import {PlayersService} from "../../../services/players.service";
import {CurrencyPipe, DatePipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {IGameStats} from "../../../interfaces/player/IGameStats";
import {PipesModule} from "../../../pipes/pipes.module";
import {GameService} from "../../../services/game.service";
import {ActiveUserService} from "../../../services/active-user.service";

@Component({
  selector: 'app-manage-player-general-info',
  standalone: true,
  imports: [
    MatCardModule,
    MatButton,
    MatIcon,
    HasPermissionDirective,
    DatePipe,
    CurrencyPipe,
    PipesModule
  ],
  templateUrl: './manage-player-general-info.component.html',
  styleUrl: './manage-player-general-info.component.css'
})
export class ManagePlayerGeneralInfoComponent {
  private matDialog: MatDialog = inject(MatDialog);
  private dialogFunctionService: DialogFunctionService = inject(DialogFunctionService);
  private playerService: PlayersService = inject(PlayersService);
  private router: Router = inject(Router);
  private gameService: GameService = inject(GameService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  // Audits are for Causable admins; a game's own admins get the player's history tabs instead.
  protected isCausableAdmin: boolean = this.activeUserService.activeUser()?.CausableAdmin ?? false;

  @Output() public getFreshPlayerInfoEvent: EventEmitter<boolean> = new EventEmitter();

  @Input()
  public set gamePlayerData(gamePlayerDataP: IGamePlayerInputData) {
    if (gamePlayerDataP.playerId && gamePlayerDataP.game) {
      this.playerId = gamePlayerDataP.playerId;
      this.playerInfo = gamePlayerDataP.playerInfo;
      this.gameId = gamePlayerDataP.game.Id;
      this.gameStats = gamePlayerDataP.playerInfo?.PlayerGameStats.filter(stats => stats.GameId == this.gameId)[0];
    }
  }

  protected gameId: string = '';
  protected playerId: string = '';
  protected PermissionTypes = PermissionTypes;
  public playerInfo: IGeneralPlayerInfo | undefined = undefined;
  public gameStats: IGameStats | undefined = undefined;

  public onUpdateBasicInfoClick() {
    const dialog_ref = this.matDialog.open(UpdateBasicPlayerInfoComponent, {
      data: {
        playerData : this.playerInfo,
        gameId: this.gameId
      },
      width: '650px'
    });
    dialog_ref.afterClosed().subscribe({
      next: (res) => {
        if (res) {
          this.getFreshPlayerInfoEvent.emit(true);
        }
      }
    })
  }

  public async onViewPlayerAuditsClick() {
    const active_game_id = this.gameService.activeGame()?.Id;
    const auditing = active_game_id ? `/${active_game_id}/auditing` : '/auditing';
    await this.router.navigateByUrl(`${auditing}?playerId=${this.playerInfo?.Id!}`);
  }

  public onDeactivatePlayerClick() {
    this.dialogFunctionService.setCallback(() => {
      return this.playerService.deactivatePlayer({
        playerId: this.playerInfo?.Id!,
        gameId: this.gameId
      })
    });

    if (this.playerInfo) {
      let deactivatePlayerDialogData: IDialogGenericData = {
        title: 'Confirm Player Deactivation',
        message: `Please confirm that you would like to deactivate the following player`,
        playerData: {
          firstName: this.playerInfo.FirstName,
          email: this.playerInfo.Email,
          lastName: this.playerInfo.LastName
        },
        dataCyTag: 'confirm-deactivate-player-button',
        successMessage: 'Successfully deactivated player',
        extraDetails: 'Deactivating a player will remove all of their numbers from all games, except any numbers already played for for any active games. The user will no longer have access to any games.'
      };

      const dialogRef = this.matDialog.open(ConfirmationActionDialogComponent, {
        data: deactivatePlayerDialogData,
        width: '650px'
      });

      dialogRef.afterClosed().subscribe(
        {
          next: (updateSuccessful: boolean) => {
            if (updateSuccessful) {
              this.getFreshPlayerInfoEvent.emit(true);
            }
          }
        })
    }
  }

  public onReactivatePlayerClick() {
    this.dialogFunctionService.setCallback(() => {
      return this.playerService.reactivatePlayer({
        playerId: this.playerInfo?.Id!,
        gameId: this.gameId
      })
    });

    if (this.playerInfo) {
      let reactivatePlayerDialogData: IDialogGenericData = {
        title: 'Confirm Player Reactivation',
        message: `Please confirm that you would like to reactivate the following player`,
        playerData: {
          firstName: this.playerInfo.FirstName,
          email: this.playerInfo.Email,
          lastName: this.playerInfo.LastName
        },
        dataCyTag: 'confirm-reactivate-player-button',
        successMessage: 'Successfully reactivated player',
        extraDetails: 'Reactivating a player allows them to log back into any game, while their numbers remain deactivated. You can selectively reactivate numbers per game or strip using the "Reactivate Strip" feature.'
      };

      const dialogRef = this.matDialog.open(ConfirmationActionDialogComponent, {
        data: reactivatePlayerDialogData,
        width: '650px'
      });

      dialogRef.afterClosed().subscribe(
        {
          next: (updateSuccessful: boolean) => {
            if (updateSuccessful) {
              this.getFreshPlayerInfoEvent.emit(true);
            }
          }
        })
    }
  }

  public onUnlockAccountClick() {
    this.dialogFunctionService.setCallback(() => {
      return this.playerService.unlockPlayerAccount({
        playerId: this.playerInfo?.Id!,
        gameId: this.gameId
      });
    });

    if (this.playerInfo) {
      let unlockAccountDialogData: IDialogGenericData = {
        title: 'Confirm Account Unlock',
        message: `Please confirm that you would like to unlock the account for the following player`,
        playerData: {
          firstName: this.playerInfo.FirstName,
          email: this.playerInfo.Email,
          lastName: this.playerInfo.LastName
        },
        dataCyTag: 'confirm-unlock-player-account-button',
        successMessage: 'Successfully unlocked player account',
        extraDetails: 'Unlocking an account will reset their failed login attempt counter and allow the player to log in again.'
      };

      const dialogRef = this.matDialog.open(ConfirmationActionDialogComponent, {
        data: unlockAccountDialogData,
        width: '650px'
      });

      dialogRef.afterClosed().subscribe({
        next: (updateSuccessful: boolean) => {
          if (updateSuccessful) {
            this.getFreshPlayerInfoEvent.emit(true);
          }
        }
      });
    }
  }
}
